import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, ChevronLeft, UtensilsCrossed, Check } from 'lucide-react'
import { api } from '@/lib/api'

const DIET_TYPES = [
  { id: 'balanced', label: 'Balanced' },
  { id: 'high_protein', label: 'High Protein' },
  { id: 'keto', label: 'Keto' },
  { id: 'low_carb', label: 'Low Carb' },
  { id: 'mediterranean', label: 'Mediterranean' },
  { id: 'vegan', label: 'Vegan' },
  { id: 'vegetarian', label: 'Vegetarian' },
  { id: 'paleo', label: 'Paleo' },
  { id: 'gluten_free', label: 'Gluten-Free' },
  { id: 'dairy_free', label: 'Dairy-Free' },
]

const GROUP_PRESETS = [
  { value: 1, label: 'Just Me' },
  { value: 2, label: '2 People' },
  { value: 4, label: 'Family of 4' },
  { value: 6, label: '6 People' },
]

interface OnboardingData {
  default_days: number
  default_meals_per_day: number
  default_group_size: number
  diet_types: string[]
  goal_mode: 'calories' | 'macros'
  calorie_target: number | null
  protein_target_g: number | null
  carbs_target_g: number | null
  fat_target_g: number | null
  fiber_target_g: number | null
}

const STEPS = [
  'Planning Scope',
  'Who You\'re Planning For',
  'Dietary Preferences',
  'Nutritional Goals',
  'Done!',
]

export default function OnboardingWizard({ onComplete }: { onComplete: () => void }) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const [step, setStep] = useState(0)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [data, setData] = useState<OnboardingData>({
    default_days: 7,
    default_meals_per_day: 3,
    default_group_size: 1,
    diet_types: [],
    goal_mode: 'calories',
    calorie_target: 2000,
    protein_target_g: null,
    carbs_target_g: null,
    fat_target_g: null,
    fiber_target_g: null,
  })

  const saveProfile = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated. Please sign in again.')
      return api.patch('/users/profile', data, token)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] })
      onComplete()
    },
    onError: (err: Error) => {
      setSaveError(err.message ?? 'Something went wrong. Please try again.')
    },
  })

  function next() {
    if (step < STEPS.length - 1) setStep(step + 1)
  }
  function back() {
    if (step > 0) setStep(step - 1)
  }

  function toggleDiet(id: string) {
    setData((d) => ({
      ...d,
      diet_types: d.diet_types.includes(id)
        ? d.diet_types.filter((x) => x !== id)
        : [...d.diet_types, id],
    }))
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-600 to-brand-800 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-white mb-2">
            <UtensilsCrossed className="w-8 h-8" />
            <span className="text-3xl font-bold">MealMap</span>
          </div>
          <p className="text-brand-100">Let's set up your meal planning preferences</p>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-6">
          {STEPS.map((s, i) => (
            <div key={i} className="flex-1 flex items-center gap-2">
              <div className={`w-full h-1.5 rounded-full transition-all ${
                i <= step ? 'bg-white' : 'bg-white/30'
              }`} />
            </div>
          ))}
        </div>

        {/* Card */}
        <div className="card p-6">
          <p className="text-xs font-semibold text-brand-600 uppercase tracking-wide mb-1">
            Step {step + 1} of {STEPS.length}
          </p>
          <h2 className="text-xl font-bold text-gray-900 mb-6">{STEPS[step]}</h2>

          {/* Step 0: Planning Scope */}
          {step === 0 && (
            <div className="space-y-5">
              <div>
                <label className="label">How many days do you want to plan?</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range" min={1} max={14} value={data.default_days}
                    onChange={(e) => setData((d) => ({ ...d, default_days: +e.target.value }))}
                    className="flex-1 accent-brand-600"
                  />
                  <span className="w-16 text-center font-bold text-lg text-brand-600">
                    {data.default_days} {data.default_days === 1 ? 'day' : 'days'}
                  </span>
                </div>
              </div>
              <div>
                <label className="label">How many meals per day?</label>
                <div className="flex gap-2">
                  {[2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => setData((d) => ({ ...d, default_meals_per_day: n }))}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition-colors ${
                        data.default_meals_per_day === n
                          ? 'border-brand-600 bg-brand-50 text-brand-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1.5">You can customize individual days later</p>
              </div>
            </div>
          )}

          {/* Step 1: Group Size */}
          {step === 1 && (
            <div className="space-y-5">
              <p className="text-sm text-gray-600">
                This helps calculate ingredient quantities and shopping amounts for your whole group.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {GROUP_PRESETS.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => setData((d) => ({ ...d, default_group_size: p.value }))}
                    className={`py-3 rounded-xl text-sm font-semibold border-2 transition-colors ${
                      data.default_group_size === p.value
                        ? 'border-brand-600 bg-brand-50 text-brand-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div>
                <label className="label">Or enter a custom number (1–20)</label>
                <input
                  type="number" min={1} max={20} value={data.default_group_size}
                  onChange={(e) => setData((d) => ({ ...d, default_group_size: Math.min(20, Math.max(1, +e.target.value)) }))}
                  className="input w-32"
                />
              </div>
              <p className="text-xs text-gray-500">
                You can override the group size for specific days or individual meals.
              </p>
            </div>
          )}

          {/* Step 2: Diet Preferences */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Select all that apply. You can change these anytime.</p>
              <div className="grid grid-cols-2 gap-2">
                {DIET_TYPES.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => toggleDiet(d.id)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border-2 transition-colors ${
                      data.diet_types.includes(d.id)
                        ? 'border-brand-600 bg-brand-50 text-brand-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {data.diet_types.includes(d.id) && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                    {d.label}
                  </button>
                ))}
              </div>
              {data.diet_types.length === 0 && (
                <p className="text-xs text-gray-400 italic">No preferences selected — we'll suggest balanced meals</p>
              )}
            </div>
          )}

          {/* Step 3: Nutritional Goals */}
          {step === 3 && (
            <div className="space-y-5">
              <div className="flex gap-3">
                {(['calories', 'macros'] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setData((d) => ({ ...d, goal_mode: mode }))}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition-colors ${
                      data.goal_mode === mode
                        ? 'border-brand-600 bg-brand-50 text-brand-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {mode === 'calories' ? '🔢 Calorie Target' : '⚗️ Full Macros'}
                  </button>
                ))}
              </div>

              {data.goal_mode === 'calories' ? (
                <div>
                  <label className="label">Daily calorie target (per person)</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number" min={500} max={5000} step={50}
                      value={data.calorie_target ?? 2000}
                      onChange={(e) => setData((d) => ({ ...d, calorie_target: +e.target.value }))}
                      className="input w-36"
                    />
                    <span className="text-sm text-gray-500">kcal / day</span>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { key: 'protein_target_g', label: 'Protein (g)', placeholder: '150' },
                    { key: 'carbs_target_g', label: 'Carbs (g)', placeholder: '200' },
                    { key: 'fat_target_g', label: 'Fat (g)', placeholder: '65' },
                    { key: 'fiber_target_g', label: 'Fiber (g)', placeholder: '30' },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key}>
                      <label className="label">{label}</label>
                      <input
                        type="number" min={0} placeholder={placeholder}
                        value={(data[key as keyof OnboardingData] as number | null) ?? ''}
                        onChange={(e) => setData((d) => ({ ...d, [key]: +e.target.value || null }))}
                        className="input"
                      />
                    </div>
                  ))}
                  <p className="col-span-2 text-xs text-gray-500">All targets are per person, per day</p>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Done */}
          {step === 4 && (
            <div className="text-center py-4 space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">You're all set!</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Planning for <strong>{data.default_group_size} {data.default_group_size === 1 ? 'person' : 'people'}</strong>,{' '}
                  <strong>{data.default_days} days</strong> a week,{' '}
                  <strong>{data.default_meals_per_day} meals</strong> a day.
                </p>
              </div>
              <div className="text-sm text-gray-500">
                All settings can be changed anytime in your profile.
              </div>
            </div>
          )}

          {/* Navigation */}
          {saveError && (
            <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {saveError}
            </p>
          )}
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={back}
              disabled={step === 0}
              className="btn-secondary flex items-center gap-1.5 disabled:invisible"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>

            {step < STEPS.length - 1 ? (
              <button onClick={next} className="btn-primary flex items-center gap-1.5">
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() => { setSaveError(null); saveProfile.mutate() }}
                disabled={saveProfile.isPending}
                className="btn-primary flex items-center gap-1.5"
              >
                {saveProfile.isPending ? 'Saving…' : 'Start Planning'}
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
